import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPosts, deletePost } from '../../lib/store';
import { formatDate } from '../../lib/utils';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiEdit, FiTrash2, FiDownload, FiEye, FiEyeOff } from 'react-icons/fi';

const Dashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await getPosts(false); // Fetch all posts (published and drafts)
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isAuth = localStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchPosts();
  }, [navigate]);

  const handleDelete = async (id) => {
    if (window.confirm('Bist du sicher, dass du diesen Beitrag löschen möchtest?')) {
      try {
        await deletePost(id);
        fetchPosts();
      } catch (error) {
        alert('Fehler beim Löschen des Beitrags.');
      }
    }
  };

  const handleExport = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(posts, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href",     dataStr);
    downloadAnchorNode.setAttribute("download", "blog_backup.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_auth');
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>Dashboard | Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-finance-blue">Beiträge verwalten</h1>
          <p className="text-slate-500">Hier kannst du deine Artikel erstellen und bearbeiten.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-md font-medium transition"
          >
            <SafeIcon icon={FiDownload} /> Backup
          </button>
          <Link 
            to="/admin/editor"
            className="flex items-center gap-2 bg-finance-green hover:bg-emerald-600 text-white px-4 py-2 rounded-md font-medium transition"
          >
            <SafeIcon icon={FiPlus} /> Neuer Beitrag
          </Link>
          <button 
            onClick={handleLogout}
            className="text-slate-500 hover:text-finance-blue ml-4 text-sm font-medium"
          >
            Abmelden
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-finance-green"></div>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="py-4 px-6 font-semibold text-slate-700">Titel</th>
                <th className="py-4 px-6 font-semibold text-slate-700">Status</th>
                <th className="py-4 px-6 font-semibold text-slate-700">Datum</th>
                <th className="py-4 px-6 font-semibold text-slate-700 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-slate-500">
                    Noch keine Beiträge vorhanden.
                  </td>
                </tr>
              ) : (
                posts.map(post => (
                  <tr key={post.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                    <td className="py-4 px-6">
                      <div className="font-medium text-finance-blue">{post.title}</div>
                      <div className="text-xs text-slate-400">{post.category}</div>
                    </td>
                    <td className="py-4 px-6">
                      {post.is_published ? (
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                          <SafeIcon icon={FiEye} /> Veröffentlicht
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded">
                          <SafeIcon icon={FiEyeOff} /> Entwurf
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-slate-600">{formatDate(post.date)}</td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end gap-3">
                        <Link 
                          to={`/admin/editor/${post.id}`}
                          className="p-2 text-slate-400 hover:text-finance-blue hover:bg-slate-200 rounded transition"
                          title="Bearbeiten"
                        >
                          <SafeIcon icon={FiEdit} />
                        </Link>
                        <button 
                          onClick={() => handleDelete(post.id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                          title="Löschen"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default Dashboard;